import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { first } from "lodash";
import { useEffect, useMemo } from "react";

import { config } from "@/config";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiCoinMetadata, useSuiCoins } from "@/hooks/sui/queries";
import currency from "@/lib/currency";

import { CurrentAccountCoinObjectsProps } from "./CurrentAccountCoinObjects.types";

export const CurrentAccountCoinObjects: React.FC<CurrentAccountCoinObjectsProps> = () => {
  const account = useCurrentAccount();

  const { setCurrentAccountCoins, transactionOutput, setTruthCoinBalance } = useSuiQueryContext();

  // MIGRATION: `getCoins` -> `core.listCoins`, `getCoinMetadata` ->
  // `core.getCoinMetadata`. Coin objects now carry `objectId` (was
  // `coinObjectId`) and the page lives under `objects` (was `data`).
  const { data, isPending, error, refetch, isFetched } = useSuiCoins({
    owner: account?.address,
    coinType: config.coinType,
  });

  const truthCoinsQuery = useSuiCoins({
    owner: account?.address,
    coinType: config.truthCoinType,
  });

  const truthMetaQuery = useSuiCoinMetadata(config.truthCoinType);

  const coinData = useMemo(() => {
    if (!data?.objects || data.objects.length === 0) {
      return null;
    }

    const coinWithHighestBalance = data.objects.reduce((highest, current) => {
      if (!highest) return current;

      return BigInt(current.balance) > BigInt(highest.balance) ? current : highest;
    }, first(data.objects));

    const totalCoinBalance = data.objects.reduce((sum, curr) => sum + BigInt(curr.balance), 0n);

    const totalCoinBalanceText = currency.convert.toDecimalsPrecisionString(
      totalCoinBalance.toString(),
      6,
    );

    return {
      // Core-API coins expose `type` (the full `0x2::coin::Coin<T>` type) rather
      // than JSON-RPC's `coinType` (the bare `T`), so echo back what we queried.
      coinType: config.coinType || "",
      coins: data.objects,
      coinWithHighestBalance,
      totalCoinBalance: {
        raw: totalCoinBalance,
        text: totalCoinBalanceText,
        formatted: `${currency.convert.toFormattedString(totalCoinBalanceText)} ${currency.constants.DEFAULT_CURRENCY_TICKER}/SUI`,
      },
    };
  }, [data?.objects]);

  useEffect(() => {
    if (coinData) {
      setCurrentAccountCoins({ ...coinData, refetch });
    }
  }, [coinData, setCurrentAccountCoins]);

  useEffect(() => {
    const truthCoins = truthCoinsQuery.data?.objects || [];

    if (!truthCoins || truthCoins.length === 0) {
      setTruthCoinBalance(undefined);

      return;
    }

    const decimals = truthMetaQuery.data?.decimals ?? 6;
    const totalTruth = truthCoins.reduce((sum, curr) => sum + BigInt(curr.balance), 0n);
    const totalTruthText = currency.convert.toDecimalsPrecisionString(
      totalTruth.toString(),
      decimals,
    );

    setTruthCoinBalance({
      raw: totalTruth,
      text: totalTruthText,
      formatted: `${currency.convert.toFormattedDigits(totalTruthText)} TRUTH`,
    });
  }, [truthCoinsQuery.data?.objects, truthMetaQuery.data?.decimals, setTruthCoinBalance]);

  useEffect(() => {
    if (!isFetched) return;

    refetch();
  }, [refetch, isFetched, transactionOutput.mergeCoinObjects.onSuccess]);

  if (error) {
    return null;
  }

  if (isPending) {
    return null;
  }

  return null;
};
